import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Divider,
  Chip,
} from '@mui/material';
import { IoPersonRemoveOutline, IoClose, IoShieldCheckmarkOutline } from 'react-icons/io5';
import { CohortStudent, CohortWithDetails } from 'src/types/cohort.types';

interface UnenrollModalProps {
  open: boolean;
  onClose: () => void;
  cohort: CohortWithDetails | null;
  studentsToUnenroll: CohortStudent[];
  onConfirmUnenroll: (studentIds: string[]) => Promise<void>;
  isUnenrolling?: boolean;
}

export function UnenrollModal({
  open,
  onClose,
  cohort,
  studentsToUnenroll,
  onConfirmUnenroll,
  isUnenrolling = false,
}: UnenrollModalProps) {
  if (!cohort || studentsToUnenroll.length === 0) return null;

  const count = studentsToUnenroll.length;
  const isMultiple = count > 1;

  const handleConfirm = async () => {
    const ids = studentsToUnenroll.map((s) => s.studentId || s.id);
    await onConfirmUnenroll(ids);
  };

  return (
    <Dialog
      open={open}
      onClose={() => !isUnenrolling && onClose()}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: '1px solid #FECACA',
          boxShadow: '0 20px 40px rgba(15, 23, 42, 0.12)',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1.5, pt: 2.5, px: 3 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: '#FEE2E2',
                color: '#DC2626',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <IoPersonRemoveOutline size={20} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={800} color="#0F172A">
                {isMultiple ? `Unenroll ${count} Scholars` : 'Unenroll Scholar'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                From cohort section <strong>{cohort.name}</strong>
              </Typography>
            </Box>
          </Stack>
          <IconButton size="small" onClick={onClose} disabled={isUnenrolling} sx={{ color: '#94A3B8' }}>
            <IoClose size={18} />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Divider sx={{ borderColor: '#F1F5F9' }} />

      <DialogContent sx={{ px: 3, py: 2 }}>
        <Stack spacing={2}>
          {isMultiple ? (
            <Typography variant="body2" color="#334155" fontSize="0.8125rem">
              Are you sure you want to unenroll <strong>{count} selected scholars</strong> from{' '}
              <strong>{cohort.name}</strong>?
            </Typography>
          ) : (
            <Box sx={{ p: 1.5, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}>
              <Typography variant="subtitle2" fontWeight={800} color="#0F172A">
                {studentsToUnenroll[0].firstname} {studentsToUnenroll[0].lastname}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {studentsToUnenroll[0].email} • Grade {studentsToUnenroll[0].grade}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: '#F0FDF4',
              border: '1px solid #BBF7D0',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Box sx={{ color: '#16A34A', mt: 0.2 }}>
                <IoShieldCheckmarkOutline size={17} />
              </Box>
              <Typography variant="caption" color="#15803D" fontSize="0.75rem" lineHeight={1.4}>
                Unenrolling removes them from this section roster. Their student accounts, wallet balances, and historical records remain 100% safe.
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <Divider sx={{ borderColor: '#F1F5F9' }} />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          disabled={isUnenrolling}
          sx={{
            color: '#64748B',
            fontWeight: 600,
            fontSize: '0.8125rem',
            textTransform: 'none',
            '&:hover': { bgcolor: '#F1F5F9' },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={isUnenrolling}
          onClick={handleConfirm}
          startIcon={isUnenrolling ? <CircularProgress size={15} color="inherit" /> : <IoPersonRemoveOutline size={15} />}
          sx={{
            fontWeight: 700,
            fontSize: '0.8125rem',
            px: 2,
            py: 0.6,
            borderRadius: 1.75,
            textTransform: 'none',
            bgcolor: '#DC2626',
            '&:hover': { bgcolor: '#B91C1C' },
          }}
        >
          {isUnenrolling ? 'Unenrolling...' : isMultiple ? `Unenroll (${count})` : 'Unenroll Scholar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
